/**
 * The "Popular Content" sidebar list: up to three Metal Market Intelligence articles, each a
 * thumbnail and a quoted title linking to the article. Despite the name, the three are the newest
 * by `publishDate` from the list it is given; nothing measures popularity.
 *
 * Callers:
 * - `./Blog.tsx` (article pages), with the six newest articles from
 *   `src/pages/metal-resources/metal-market-intelligence/[slug].tsx` and the current article's
 *   slug as `excludeSlug`.
 * - `components/NewsEvents/Events/EventDetails.tsx` (event pages at
 *   `/about-us/news-events/<eventSlug>`), with `excludeSlug` empty.
 *
 * The desktop links are absolute (`/metal-resources/metal-market-intelligence/<slug>`). The
 * mobile links are the bare slug, which Next.js resolves against the current route: right on an
 * article page, but on an event page it gives `/about-us/news-events/<slug>`, which isn't an
 * article.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { useTheme } from "@ryerson/frontend.theme";
import { Link } from "@ryerson/frontend.navigation";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ProcessedBlogEntry, TranslatedBlogEntries } from "@contentful/blog/types";

const PopularContentContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
	padding: 15px 23px 20px 23px;
	box-sizing: border-box;
	border-radius: 2px;
`;

const PopularImage = styled.div`
	display: inline-block;
	width: 80px;
	height: 80px;
	box-sizing: border-box;
	overflow: hidden;
	text-align: center;
	background-size: cover;
	background-position: left top;
	background-repeat: no-repeat;
`;

const PopularTitle = styled.div`
	display: inline-block;
	vertical-align: top;
	text-align: left;
	margin-left: 15px;
	width: calc(100% - 95px);
`;

const Divider = styled.hr`
	margin-top: 0px;
	margin-bottom: 0px;
	opacity: 0.1;
`;

/** Props of {@link PopularContent}. */
export type PopularContentProps = {
	/** Slug to leave out, normally the article being read. `""` keeps every article. */
	excludeSlug: string;
	/** The list heading, already in the visitor's language. */
	title: string;
	/** Articles per language; only the visitor's language is read. */
	blogContent: TranslatedBlogEntries;
};

/** Renders the three newest articles other than `excludeSlug`; see the file header. */
export const PopularContent: React.FC<PopularContentProps> = ({
	excludeSlug,
	title,
	blogContent,
}) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	let allBlogs: ProcessedBlogEntry[] = [];

	blogContent[language]?.forEach((node) => {
		if (node?.slug !== excludeSlug) {
			allBlogs.push(node);
		}
	});
	allBlogs.sort(function (a: ProcessedBlogEntry, b: ProcessedBlogEntry) {
		return new Date(b.publishDate).valueOf() - new Date(a.publishDate).valueOf();
	});

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<PopularContentContainer
					css={css`
						background-color: ${theme.colors.primary.background};
					`}
				>
					<Typography
						variant="h3"
						css={css`
							font-size: 20px;
							line-height: 25px;
							margin-top: 0px;
							margin-bottom: 20px;
						`}
					>
						{title}
					</Typography>
					<Divider
						css={css`
							margin-top: 0px;
							margin-bottom: 20px;
						`}
					/>
					{allBlogs.map((popularContent: any, index: number) => {
						return (
							<>
								{index < 3 && (
									<Link
										to={
											"/metal-resources/metal-market-intelligence/" +
											popularContent?.slug
										}
									>
										<PopularImage
											css={css`
												background-image: url(${popularContent?.heroImage
													?.file?.url
													? `${popularContent.heroImage?.file?.url}?fm=jpg&fit=fill&w=80&h=80`
													: ""});
											`}
										/>
										<PopularTitle>
											<Typography
												variant="div"
												size="md"
												weight="bold"
												color={theme.colors.primary.header}
											>
												{'"' + popularContent?.title + '"'}
											</Typography>
										</PopularTitle>
									</Link>
								)}
								{index < 2 && (
									<Divider
										css={css`
											margin-top: 20px;
											margin-bottom: 20px;
										`}
									/>
								)}
							</>
						);
					})}
				</PopularContentContainer>
			</Media>
			<Media lessThan="lg">
				<PopularContentContainer
					css={css`
						padding: 0px;
					`}
				>
					<Typography
						variant="h3"
						css={css`
							font-size: 20px;
							line-height: 25px;
							margin-top: 0px;
							margin-bottom: 20px;
						`}
					>
						{title}
					</Typography>
					<Divider
						css={css`
							margin-top: 0px;
							margin-bottom: 20px;
						`}
					/>
					{allBlogs.map((popularContent: any, index: number) => {
						return (
							<>
								{index < 3 && (
									/* A bare slug, resolved against the current route; see the
									file header. */
									<Link to={popularContent.slug}>
										<PopularImage
											css={css`
												height: 60px;
												width: 60px;
												background-image: url(${popularContent?.heroImage
													?.file?.url
													? `${popularContent?.heroImage?.file?.url}?fm=jpg&fit=fill&w=60&h=60`
													: ""});
											`}
										/>
										<PopularTitle>
											<Typography
												variant="div"
												size="md"
												weight="bold"
												color={theme.colors.primary.header}
											>
												{'"' + popularContent?.title + '"'}
											</Typography>
										</PopularTitle>
									</Link>
								)}
								{index < 2 && (
									<Divider
										css={css`
											margin-top: 20px;
											margin-bottom: 20px;
										`}
									/>
								)}
							</>
						);
					})}
				</PopularContentContainer>
			</Media>
		</>
	);
};
